import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff } from "lucide-react";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { AuthShell } from "@/pages/Login";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await register(form.name, form.email, form.password);
      navigate("/");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Create your account" subtitle="Start with the free foundation guide — upgrade anytime.">
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="name">Full name</Label>
          <Input id="name" data-testid="register-name" value={form.name} onChange={set("name")} className="h-11" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input id="email" data-testid="register-email" type="email" value={form.email} onChange={set("email")} className="h-11" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password">Password</Label>
          <div className="relative">
            <Input id="password" data-testid="register-password" type={showPassword ? "text" : "password"} value={form.password} onChange={set("password")} className="h-11 pr-11" />
            <button type="button" data-testid="register-password-toggle" onClick={() => setShowPassword((v) => !v)} aria-label={showPassword ? "Hide password" : "Show password"} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition-colors">
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        {err && <p className="text-sm text-red-600" data-testid="register-error">{err}</p>}
        <Button type="submit" data-testid="register-submit" disabled={loading} className="w-full h-11 rounded-full bg-brand hover:bg-brand-hover text-white font-bold">
          {loading ? "Creating…" : "Create account"}
        </Button>
      </form>
      <p className="text-sm text-slate-500 mt-4 text-center">
        Already have an account? <Link to="/login" className="text-brand font-bold">Log in</Link>
      </p>
      <p className="text-xs text-slate-400 mt-3 text-center">
        By creating an account you agree to our <Link to="/terms" data-testid="register-terms-link" className="text-slate-500 underline hover:text-brand">Terms &amp; Conditions</Link> and <Link to="/privacy" data-testid="register-privacy-link" className="text-slate-500 underline hover:text-brand">Privacy Policy</Link>.
      </p>
    </AuthShell>
  );
}
